<template>
  <div>
    <base-header></base-header>
    <div class="container-fluid mt--5">
      <card>
        <div slot="header" class="d-flex justify-content-between align-items-center">
          <div>
            <h3 class="mb-0">Blueprint de fases: {{ projectTypeName }}</h3>
            <small class="text-muted">Arrastra una fase para reubicarla, doble click para editarla. Para crear una transición, arrastra desde el punto azul del borde de una fase hacia otra (puedes arrastrar varias veces desde el mismo punto para crear varias salidas).</small>
          </div>
          <div>
            <v-btn color="primary" small @click="openCreatePhase">+ Nueva fase</v-btn>
            <v-btn text small @click="$router.push('/project-types')">Volver</v-btn>
          </div>
        </div>

        <div v-if="warnings.length" class="px-3 pt-3">
          <div class="alert alert-warning py-2 px-3 mb-2" v-for="(w, i) in warnings" :key="i">{{ w }}</div>
        </div>

        <div class="blueprint-canvas" ref="canvas">
          <div
            v-for="phase in phases"
            :key="phase.id + ':' + renderEpoch"
            :id="nodeDomId(phase.id)"
            class="blueprint-node"
            :class="{ 'blueprint-node--start': phase.isStart }"
            :style="{ left: (phase.positionX || 100) + 'px', top: (phase.positionY || 100) + 'px' }"
            @dblclick="openEditPhase(phase)"
          >
            <div class="blueprint-node__label">{{ nodeLabel(phase) }}</div>
          </div>
        </div>
      </card>
    </div>

    <!-- Modal: crear/editar fase -->
    <v-dialog v-model="phaseDialog.show" max-width="460px">
      <v-card>
        <v-card-title>{{ phaseDialog.isNew ? "Nueva fase" : "Editar fase" }}</v-card-title>
        <v-card-text>
          <v-text-field label="Nombre" v-model="phaseDialog.form.name" dense></v-text-field>
          <div class="row">
            <div class="col-6">
              <v-text-field label="Valor SLA" type="number" v-model.number="phaseDialog.form.slaValue" dense></v-text-field>
            </div>
            <div class="col-6">
              <v-select
                label="Unidad"
                v-model="phaseDialog.form.slaUnit"
                :items="slaUnitOptions"
                item-text="name"
                item-value="id"
                dense
              ></v-select>
            </div>
          </div>
          <v-alert v-if="!phaseDialog.isNew" dense text type="info" class="mb-3">
            Este cambio no afecta ciclos ya iniciados.
          </v-alert>
          <v-checkbox v-model="phaseDialog.form.isStart" label="Fase de inicio" dense hide-details class="mb-2"></v-checkbox>
          <v-select
            label="Al completar (salida)"
            v-model="phaseDialog.form.splitType"
            :items="splitTypeOptions"
            item-text="name"
            item-value="id"
            dense
          ></v-select>
          <v-select
            label="Para arrancar (entrada)"
            v-model="phaseDialog.form.joinType"
            :items="joinTypeOptions"
            item-text="name"
            item-value="id"
            dense
          ></v-select>
          <v-autocomplete
            label="Responsable por defecto (opcional)"
            v-model="phaseDialog.form.defaultResponsibleUserId"
            :items="$store.state.users.users"
            item-text="name"
            item-value="id"
            clearable
            dense
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!phaseDialog.isNew" text color="error" @click="confirmDeactivatePhase">Eliminar</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="phaseDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="phaseDialog.saving" @click="handleSavePhase">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: editar/eliminar transicion -->
    <v-dialog v-model="transitionDialog.show" max-width="420px">
      <v-card>
        <v-card-title>Transición: {{ transitionDialog.fromName }} → {{ transitionDialog.toName }}</v-card-title>
        <v-card-text>
          <v-text-field label="Etiqueta (opcional)" v-model="transitionDialog.form.label" dense></v-text-field>
          <v-select
            label="Lado de salida"
            v-model="transitionDialog.form.fromAnchor"
            :items="anchorOptions"
            item-text="name"
            item-value="id"
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" :loading="transitionDialog.saving" @click="handleDeleteTransition">Eliminar</v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="transitionDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="transitionDialog.saving" @click="handleSaveTransition">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <base-confirm
      ref="deactivatePhaseConfirm"
      :text="`¿Eliminar la fase '${pendingPhase ? pendingPhase.name : ''}'? Sus transiciones también se eliminarán.`"
      @acept="handleDeactivatePhaseConfirmed"
    ></base-confirm>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import { jsPlumb } from "jsplumb";

const SPLIT_TYPES = [
  { id: 0, name: "Secuencial (una sola salida)" },
  { id: 1, name: "Exclusiva (el usuario elige un camino)" },
  { id: 2, name: "Paralela (dispara todas las salidas)" },
];

const JOIN_TYPES = [
  { id: 0, name: "Ninguno" },
  { id: 1, name: "Todas las predecesoras (AND-join)" },
];

export default {
  name: "phase-blueprint",
  data() {
    return {
      projectTypeId: this.$route.params.project_type_id,
      projectTypeName: "",
      phases: [],
      transitions: [],
      warnings: [],
      renderEpoch: 0,
      slaUnitOptions: [
        { id: 0, name: "Días laborables" },
        { id: 1, name: "Horas laborables" },
      ],
      splitTypeOptions: SPLIT_TYPES,
      joinTypeOptions: JOIN_TYPES,
      anchorOptions: [
        { id: "bottom", name: "Abajo" },
        { id: "right", name: "Derecha" },
      ],
      phaseDialog: { show: false, isNew: true, saving: false, form: this.emptyPhaseForm() },
      pendingPhase: null,
      transitionDialog: {
        show: false,
        saving: false,
        fromName: "",
        toName: "",
        existingId: null,
        form: { label: "" },
      },
    };
  },
  async mounted() {
    // jsPlumbInstance no se declara en data() a proposito -- es un objeto imperativo,
    // no debe volverse reactivo (Vue lo recorreria innecesariamente en cada cambio).
    this.jsPlumbInstance = jsPlumb.getInstance({
      Connector: ["Flowchart", { cornerRadius: 0, stub: 20 }],
      PaintStyle: { stroke: "#5e72e4", strokeWidth: 2 },
      HoverPaintStyle: { stroke: "#2dce89", strokeWidth: 2 },
      ConnectionOverlays: [["Arrow", { location: 1, width: 10, length: 10 }]],
      Container: this.$refs.canvas,
    });

    this.jsPlumbInstance.bind("connection", this.handleConnectionCreated);
    this.jsPlumbInstance.bind("click", this.handleConnectionClick);

    await this.load();
  },
  beforeDestroy() {
    if (this.jsPlumbInstance) {
      this.jsPlumbInstance.reset();
      this.jsPlumbInstance = null;
    }
  },
  methods: {
    ...mapActions([
      "getProjectTypes",
      "getPhaseBlueprint",
      "createProjectTypePhase",
      "updateProjectTypePhase",
      "deactivateProjectTypePhase",
      "updatePhasePositions",
      "createPhaseTransition",
      "updatePhaseTransition",
      "deactivatePhaseTransition",
    ]),

    nodeDomId(phaseId) {
      return `pbnode-${phaseId}`;
    },

    phaseIdFromDomId(domId) {
      return domId.replace(/^pbnode-/, "");
    },

    // Identifica, por identidad de objeto, de cual de los dos Endpoints fuente de la fase
    // (bottom/right) salio un arrastre en curso, para poder guardarlo junto con la transicion.
    anchorFromEndpoint(phaseId, endpoint) {
      const endpoints = this.sourceEndpointsByPhase?.[phaseId];
      if (!endpoints) return null;
      if (endpoint === endpoints.right) return "right";
      if (endpoint === endpoints.bottom) return "bottom";
      return null;
    },

    emptyPhaseForm() {
      return {
        id: undefined,
        name: "",
        slaValue: 1,
        slaUnit: 0,
        isStart: false,
        splitType: 0,
        joinType: 0,
        positionX: 100,
        positionY: 100,
        defaultResponsibleUserId: undefined,
      };
    },

    async load() {
      const types = await this.getProjectTypes();
      const type = (types || []).find((t) => t.id === this.projectTypeId);
      this.projectTypeName = type ? type.name : "";

      const blueprint = await this.getPhaseBlueprint(this.projectTypeId);
      if (!blueprint) return;

      this.warnings = blueprint.warnings || [];
      this.phases = blueprint.phases;
      this.transitions = blueprint.transitions;
      this.renderEpoch++;

      await this.$nextTick();
      this.setupDiagram();
    },

    // Reconstruye el diagrama por completo en cada carga -- el :key con renderEpoch
    // fuerza a Vue a crear elementos DOM nuevos, asi que no hay riesgo de que jsPlumb
    // quede con listeners duplicados de drag/conexion sobre un nodo reciclado.
    setupDiagram() {
      const jp = this.jsPlumbInstance;
      if (!jp) return;

      jp.reset(true);

      // Dos Endpoints fuente por fase (centro del borde inferior y centro del borde derecho,
      // anclados en el propio nodo, sin un div de handle aparte), cada uno reutilizado para
      // todas las transiciones salientes que se dibujen desde ese lado. Si en vez de esto
      // cada transicion se dibuja con jp.connect({source: domId}), jsPlumb crea un endpoint
      // nuevo en ese punto con el limite de conexiones por defecto (1): al arrastrar de nuevo
      // desde el mismo punto, ese endpoint agarra la conexion ya existente en vez de disparar
      // la creacion de una nueva.
      // Nota: un div de handle separado (posicionado encima con CSS) tapaba el propio
      // elemento del Endpoint y se quedaba con el mousedown -- por eso los Endpoints se
      // anclan directamente sobre el nodo.
      // Se guarda en `this` (no reactivo) para que handleConnectionCreated pueda identificar,
      // por identidad de objeto, desde cual de los dos Endpoints arrastro el usuario.
      const sourceEndpoints = {};
      this.sourceEndpointsByPhase = sourceEndpoints;

      const makeSourceEndpoint = (domId, anchor) =>
        jp.addEndpoint(domId, {
          isSource: true,
          maxConnections: -1,
          anchor,
          endpoint: ["Dot", { radius: 7 }],
          paintStyle: { fill: "#5e72e4", stroke: "#ffffff", strokeWidth: 2 },
          hoverPaintStyle: { fill: "#2dce89" },
          cssClass: "blueprint-node__handle",
        });

      this.phases.forEach((phase) => {
        const domId = this.nodeDomId(phase.id);
        jp.draggable(domId, {
          // Excluye los handles de conexion (su propio cssClass) del drag del nodo -- si no,
          // un mousedown ahi mueve la fase entera en vez de iniciar una conexion nueva.
          filter: ".blueprint-node__handle",
          stop: (params) => this.handleDragStop(phase.id, params),
        });
        sourceEndpoints[phase.id] = {
          bottom: makeSourceEndpoint(domId, [0.5, 1, 0, 1]),
          right: makeSourceEndpoint(domId, [1, 0.5, 1, 0]),
        };
        jp.makeTarget(domId, {
          anchor: "Continuous",
          maxConnections: -1,
        });
      });

      const phasesById = this.phases.reduce((acc, p) => ((acc[p.id] = p), acc), {});

      this.transitions.forEach((transition) => {
        const endpoints = sourceEndpoints[transition.fromPhaseId];
        if (!endpoints) return;

        // Si la transicion ya sabe de que lado salir (guardado al crearla), se respeta esa
        // eleccion. Solo para transiciones viejas sin este dato se adivina por posicion.
        let source = endpoints[transition.fromAnchor];
        if (!source) {
          const from = phasesById[transition.fromPhaseId];
          const to = phasesById[transition.toPhaseId];
          const dx = (to?.positionX || 0) - (from?.positionX || 0);
          const dy = (to?.positionY || 0) - (from?.positionY || 0);
          source = Math.abs(dy) >= Math.abs(dx) ? endpoints.bottom : endpoints.right;
        }

        jp.connect({
          source,
          target: this.nodeDomId(transition.toPhaseId),
          parameters: { transitionId: transition.id },
          overlays: transition.label ? [["Label", { label: transition.label, location: 0.5, cssClass: "blueprint-edge-label" }]] : [],
          doNotFireConnectionEvent: true,
        });
      });

      jp.repaintEverything();
    },

    nodeLabel(phase) {
      const start = phase.isStart ? "▶ " : "";
      const split = phase.splitType === 1 ? " [X]" : phase.splitType === 2 ? " [||]" : "";
      const join = phase.joinType === 1 ? " [AND]" : "";
      return `${start}${phase.name}${split}${join}`;
    },

    openCreatePhase() {
      this.phaseDialog = { show: true, isNew: true, saving: false, form: this.emptyPhaseForm() };
    },

    openEditPhase(phase) {
      this.phaseDialog = {
        show: true,
        isNew: false,
        saving: false,
        form: {
          id: phase.id,
          name: phase.name,
          slaValue: phase.slaValue,
          slaUnit: phase.slaUnit,
          isStart: phase.isStart,
          splitType: phase.splitType,
          joinType: phase.joinType,
          positionX: phase.positionX,
          positionY: phase.positionY,
          defaultResponsibleUserId: phase.defaultResponsibleUserId,
        },
      };
    },

    async handleSavePhase() {
      if (!this.phaseDialog.form.name) return;
      this.phaseDialog.saving = true;
      const payload = { ...this.phaseDialog.form, projectTypeId: this.projectTypeId };
      const ok = this.phaseDialog.isNew
        ? await this.createProjectTypePhase(payload)
        : await this.updateProjectTypePhase(payload);
      this.phaseDialog.saving = false;
      if (ok) {
        this.$notify("success", "Fase guardada");
        this.phaseDialog.show = false;
        await this.load();
      }
    },

    confirmDeactivatePhase() {
      this.pendingPhase = this.phaseDialog.form;
      this.$refs.deactivatePhaseConfirm.show();
    },

    async handleDeactivatePhaseConfirmed() {
      if (!this.pendingPhase) return;
      const ok = await this.deactivateProjectTypePhase({ projectTypeId: this.projectTypeId, id: this.pendingPhase.id });
      if (ok) {
        this.$notify("success", "Fase eliminada");
        this.phaseDialog.show = false;
        await this.load();
      }
      this.pendingPhase = null;
    },

    openEditTransition(transition) {
      this.transitionDialog = {
        show: true,
        saving: false,
        fromName: transition.fromPhaseName,
        toName: transition.toPhaseName,
        existingId: transition.id,
        form: { label: transition.label || "", fromAnchor: transition.fromAnchor || "bottom" },
      };
    },

    async handleSaveTransition() {
      this.transitionDialog.saving = true;
      const ok = await this.updatePhaseTransition({
        projectTypeId: this.projectTypeId,
        id: this.transitionDialog.existingId,
        label: this.transitionDialog.form.label || null,
        fromAnchor: this.transitionDialog.form.fromAnchor,
      });
      this.transitionDialog.saving = false;
      if (ok) {
        this.$notify("success", "Transición actualizada");
        this.transitionDialog.show = false;
        await this.load();
      }
    },

    async handleDeleteTransition() {
      this.transitionDialog.saving = true;
      const ok = await this.deactivatePhaseTransition({ projectTypeId: this.projectTypeId, id: this.transitionDialog.existingId });
      this.transitionDialog.saving = false;
      if (ok) {
        this.$notify("success", "Transición eliminada");
        this.transitionDialog.show = false;
        await this.load();
      }
    },

    // Se dispara cuando el usuario suelta un arrastre desde el handle de una fase sobre
    // otra fase. jsPlumb ya dibujo la conexion optimista; se intenta persistir como
    // transicion real. Si el backend la rechaza (validacion del grafo), un load() vuelve a
    // pedir el blueprint real al servidor, que jamas incluyo la conexion optimista, asi que
    // desaparece sola al redibujar.
    async handleConnectionCreated(info) {
      const fromPhaseId = this.phaseIdFromDomId(info.sourceId);
      const toPhaseId = this.phaseIdFromDomId(info.targetId);

      const created = await this.createPhaseTransition({
        projectTypeId: this.projectTypeId,
        fromPhaseId,
        toPhaseId,
        label: null,
        fromAnchor: this.anchorFromEndpoint(fromPhaseId, info.sourceEndpoint),
      });

      if (created) {
        this.$notify("success", "Transición creada");
        if (created.model && created.model.warnings && created.model.warnings.length) {
          this.warnings = created.model.warnings;
        }
      } else {
        this.$notify("error", "No se pudo crear la transición");
      }

      await this.load();
    },

    handleConnectionClick(connection) {
      const transitionId = connection.getParameter("transitionId");
      if (!transitionId) return;
      const transition = this.transitions.find((t) => t.id === transitionId);
      if (!transition) return;
      this.openEditTransition(transition);
    },

    async handleDragStop(phaseId, params) {
      const [positionX, positionY] = params.pos;
      const phase = this.phases.find((p) => p.id === phaseId);
      if (phase) {
        phase.positionX = positionX;
        phase.positionY = positionY;
      }
      await this.updatePhasePositions({
        projectTypeId: this.projectTypeId,
        positions: [{ id: phaseId, positionX, positionY }],
      });
    },
  },
};
</script>
<style scoped>
.blueprint-canvas {
  position: relative;
  width: 100%;
  height: 620px;
  overflow: auto;
  background-color: #f8f9fe;
  background-image: radial-gradient(#d8dcf0 1px, transparent 1px);
  background-size: 16px 16px;
  border-top: 1px solid #e9ecef;
}

.blueprint-node {
  position: absolute;
  width: 150px;
  min-height: 60px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #ffffff;
  border: 1.5px solid #5e72e4;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  cursor: move;
  user-select: none;
  font-size: 0.85rem;
  color: #32325d;
}

.blueprint-node--start {
  background: #e3f2ff;
}

.blueprint-node__label {
  pointer-events: none;
}

/* El punto de conexion lo dibuja jsPlumb (endpoint Dot con paintStyle), no un div propio --
   esta clase solo ajusta el cursor sobre el elemento que jsPlumb genera. */
.blueprint-node__handle {
  cursor: crosshair;
}

.blueprint-node__handle:hover {
  background: #2dce89;
}
</style>
<style>
.blueprint-edge-label {
  background: #ffffff;
  border: 1px solid #5e72e4;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.75rem;
  color: #32325d;
}
</style>
